import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Clock, Play, Coffee, LogOut, Loader2, CheckCircle2 } from "lucide-react";

const fmt = (s) => {
  const sec = Math.max(0, s || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};
const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");

const STATE_META = {
  working: { label: "On the clock", chip: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30", dot: "bg-emerald-400" },
  on_break: { label: "On break", chip: "bg-amber-500/15 text-amber-300 border-amber-400/30", dot: "bg-amber-400" },
  clocked_out: { label: "Clocked out", chip: "bg-slate-500/15 text-slate-300 border-slate-400/30", dot: "bg-slate-400" },
};

export default function TimeClockBar() {
  const [shift, setShift] = useState(null);
  const [busy, setBusy] = useState(null);
  const [, setTick] = useState(0);
  const poll = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/timeclock/status"); setShift(data); } catch { /* transient */ }
  };
  useEffect(() => {
    load();
    poll.current = setInterval(load, 30000);
    const live = setInterval(() => setTick((t) => t + 1), 1000); // keep the live counter ticking
    const onChange = () => load(); // sync when the gate (or any other place) clocks in/out
    window.addEventListener("timeclock-changed", onChange);
    return () => { clearInterval(poll.current); clearInterval(live); window.removeEventListener("timeclock-changed", onChange); };
  }, []);

  const act = async (path, label) => {
    setBusy(path);
    try {
      const { data } = await api.post(`/timeclock/${path}`);
      setShift(data);
      toast.success(label);
      window.dispatchEvent(new Event("timeclock-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update time clock"); }
    finally { setBusy(null); }
  };

  const state = shift?.state || "clocked_out";
  const meta = STATE_META[state];
  // live worked seconds while active
  const liveWork = (() => {
    if (!shift?.clock_in) return shift?.work_seconds || 0;
    if (state === "clocked_out") return shift?.work_seconds || 0;
    const now = Date.now();
    const ci = new Date(shift.clock_in).getTime();
    let brk = 0;
    (shift.breaks || []).forEach((b) => {
      if (!b.start) return;
      const bs = new Date(b.start).getTime();
      const be = b.end ? new Date(b.end).getTime() : now;
      if (be > bs) brk += be - bs;
    });
    return Math.floor((now - ci - brk) / 1000);
  })();

  const Btn = ({ onClick, path, icon: Icon, children, primary }) => (
    <button data-testid={`clock-${path}-btn`} onClick={onClick} disabled={!!busy}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60 ${
        primary ? "bg-teal text-teal-foreground hover:bg-teal/90" : "border border-white/20 text-slate-200 hover:bg-white/10"}`}>
      {busy === path ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {children}
    </button>
  );

  return (
    <div data-testid="time-clock-bar" className="bg-navy-2 border-b border-white/5">
      <div className="flex flex-wrap items-center gap-3 px-4 lg:px-6 py-2">
        <div className="flex items-center gap-2 text-slate-300">
          <Clock className="h-4 w-4 text-teal" />
          <span className="text-[12px] font-semibold uppercase tracking-wide">Time Clock</span>
        </div>
        <span data-testid="clock-state-chip" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${meta.chip}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} ${state !== "clocked_out" ? "animate-pulse" : ""}`} /> {meta.label}
        </span>
        <div className="flex items-center gap-3 text-[12px] text-slate-300">
          <span data-testid="clock-worked" className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Worked <b className="text-white tabular-nums">{fmt(liveWork)}</b></span>
          <span className="text-slate-500">·</span>
          <span data-testid="clock-break" className="inline-flex items-center gap-1"><Coffee className="h-3.5 w-3.5 text-amber-400" /> Break <b className="text-slate-200 tabular-nums">{fmt(shift?.break_seconds)}</b></span>
          {shift?.clock_in && <><span className="text-slate-500 hidden sm:inline">·</span><span className="hidden sm:inline text-slate-400">In {timeOf(shift.clock_in)}{shift.clock_out ? ` · Out ${timeOf(shift.clock_out)}` : ""}</span></>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {state === "clocked_out" && <Btn onClick={() => act("clock-in", "Clocked in — have a great shift!")} path="clock-in" icon={Play} primary>Log In</Btn>}
          {state === "working" && <Btn onClick={() => act("break-start", "Break started")} path="break-start" icon={Coffee}>Take Break</Btn>}
          {state === "on_break" && <Btn onClick={() => act("break-end", "Welcome back")} path="break-end" icon={Play} primary>End Break</Btn>}
          {state !== "clocked_out" && <Btn onClick={() => act("clock-out", "Clocked out — see you next shift!")} path="clock-out" icon={LogOut}>Log Out</Btn>}
        </div>
      </div>
    </div>
  );
}
